import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  Clock3,
  Package,
  PoundSterling,
  RefreshCw,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '';

const PERIOD_OPTIONS = [
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '12m', label: 'Last 12 months' },
  { value: 'all', label: 'All time' },
];

const formatCurrency = (value) =>
  `£${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value) => {
  if (!value) return 'Never';
  return new Date(value).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const displayName = (customer) =>
  customer?.customer_name || customer?.display_name || customer?.username || 'Unknown customer';

const statusClass = (status) => {
  const normalised = String(status || '').toLowerCase();
  if (normalised.includes('completed')) return 'bg-emerald-100 text-emerald-800';
  if (normalised.includes('reject') || normalised.includes('cancel')) return 'bg-red-100 text-red-800';
  if (normalised === 'pending') return 'bg-amber-100 text-amber-800';
  return 'bg-blue-100 text-blue-800';
};

const MetricCard = ({ label, value, detail, icon: Icon, colour }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-sm font-medium text-slate-500">{label}</p>
        <p className="mt-2 text-2xl font-bold text-slate-900">{value}</p>
        {detail && <p className="mt-1 text-xs text-slate-500">{detail}</p>}
      </div>
      <span className={`rounded-lg p-2 ${colour}`}>
        <Icon size={20} aria-hidden="true" />
      </span>
    </div>
  </div>
);

const EmptyState = ({ children }) => (
  <p className="rounded-lg bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">{children}</p>
);

const PartnerDashboard = () => {
  const { authFetch } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [period, setPeriod] = useState('12m');
  const [dashboard, setDashboard] = useState(null);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [error, setError] = useState('');

  const loadCustomers = useCallback(async () => {
    setLoadingCustomers(true);
    setError('');
    try {
      const response = await authFetch(`${API_URL}/api/partner-dashboard/partners`);
      if (!response.ok) throw new Error('Could not load partner customers');
      const data = await response.json();
      setCustomers(Array.isArray(data) ? data : []);
      setSelectedCustomerId((current) => current || data[0]?.id || '');
    } catch (loadError) {
      setError(loadError.message || 'Could not load partner customers');
    } finally {
      setLoadingCustomers(false);
    }
  }, [authFetch]);

  const loadDashboard = useCallback(async () => {
    if (!selectedCustomerId) {
      setDashboard(null);
      return;
    }
    setLoadingDashboard(true);
    setError('');
    try {
      const response = await authFetch(
        `${API_URL}/api/partner-dashboard/${selectedCustomerId}?period=${period}`
      );
      if (!response.ok) throw new Error('Could not load customer insights');
      setDashboard(await response.json());
    } catch (loadError) {
      setError(loadError.message || 'Could not load customer insights');
      setDashboard(null);
    } finally {
      setLoadingDashboard(false);
    }
  }, [authFetch, period, selectedCustomerId]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const selectedCustomer = dashboard?.customer || customers.find((customer) => customer.id === selectedCustomerId);
  const summary = dashboard?.summary;
  const monthlyMax = useMemo(
    () => Math.max(...(dashboard?.monthly_volume || []).map((month) => month.orders), 1),
    [dashboard]
  );
  const jobMax = useMemo(
    () => Math.max(...(dashboard?.job_types || []).map((job) => job.orders), 1),
    [dashboard]
  );
  const topJob = dashboard?.job_types?.[0];
  const topFormat = dashboard?.formats?.[0];
  const latestMonthOrders = summary?.last_month_orders || 0;
  const previousMonthOrders = summary?.previous_month_orders || 0;
  const activityMessage = latestMonthOrders > previousMonthOrders
    ? `Up from ${previousMonthOrders} order${previousMonthOrders === 1 ? '' : 's'} last month`
    : latestMonthOrders < previousMonthOrders
      ? `Down from ${previousMonthOrders} order${previousMonthOrders === 1 ? '' : 's'} last month`
      : 'Stable against the previous month';

  if (loadingCustomers) {
    return <div className="py-12 text-center text-slate-500">Loading partner customers...</div>;
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">Customer intelligence</p>
            <h2 className="mt-1 text-xl font-bold text-slate-900">Partner customer dashboard</h2>
            <p className="mt-1 text-sm text-slate-500">Understand ordering behaviour and identify opportunities to win more of their work.</p>
          </div>
          <button
            type="button"
            onClick={() => { loadCustomers(); loadDashboard(); }}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            <RefreshCw size={16} aria-hidden="true" />
            Refresh
          </button>
        </div>
        <div className="mt-5 grid gap-4 md:grid-cols-[minmax(0,1fr)_220px]">
          <label className="text-sm font-medium text-slate-700">
            Customer
            <select
              value={selectedCustomerId}
              onChange={(event) => setSelectedCustomerId(event.target.value)}
              className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              data-testid="partner-customer-select"
            >
              {customers.length === 0 && <option value="">No external customers found</option>}
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {displayName(customer)} ({customer.username})
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-medium text-slate-700">
            Period
            <select
              value={period}
              onChange={(event) => setPeriod(event.target.value)}
              className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            >
              {PERIOD_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </label>
        </div>
      </section>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      {customers.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <Users className="mx-auto text-slate-400" size={36} aria-hidden="true" />
          <h3 className="mt-3 font-semibold text-slate-900">No external customers found</h3>
          <p className="mt-1 text-sm text-slate-500">External users will appear here once their accounts are created.</p>
        </div>
      ) : loadingDashboard ? (
        <div className="py-12 text-center text-slate-500">Loading customer insights...</div>
      ) : dashboard && summary ? (
        <>
          <section className="flex flex-col justify-between gap-3 rounded-xl border border-indigo-100 bg-indigo-50 px-5 py-4 sm:flex-row sm:items-center">
            <div>
              <h3 className="text-lg font-bold text-slate-900">{displayName(selectedCustomer)}</h3>
              <p className="text-sm text-slate-600">
                @{selectedCustomer?.username} · {selectedCustomer?.pricing_tier || 'retail'} pricing
                {selectedCustomer?.email ? ` · ${selectedCustomer.email}` : ''}
              </p>
            </div>
            <div className="text-left sm:text-right">
              <p className="text-xs uppercase tracking-wide text-slate-500">Last order</p>
              <p className="font-semibold text-slate-900">{formatDate(summary.last_order_at)}</p>
            </div>
          </section>

          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard label="Orders" value={summary.orders.toLocaleString()} detail={`${summary.open_orders} currently open`} icon={Package} colour="bg-blue-100 text-blue-700" />
            <MetricCard label="Units ordered" value={summary.units.toLocaleString()} detail={`${summary.items} line items`} icon={BarChart3} colour="bg-violet-100 text-violet-700" />
            <MetricCard label="Quoted value" value={formatCurrency(summary.spend)} detail={`${formatCurrency(summary.completed_spend)} completed value`} icon={PoundSterling} colour="bg-emerald-100 text-emerald-700" />
            <MetricCard label="Completion rate" value={`${summary.completion_rate}%`} detail={`${summary.completed_orders} completed orders`} icon={TrendingUp} colour="bg-amber-100 text-amber-700" />
          </section>

          <section className="grid gap-6 xl:grid-cols-[1.35fr_1fr]">
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="font-bold text-slate-900">Order volume</h3>
                  <p className="text-sm text-slate-500">Orders and units by month</p>
                </div>
                <BarChart3 className="text-slate-400" size={20} aria-hidden="true" />
              </div>
              {dashboard.monthly_volume.length ? (
                <div className="mt-6 flex h-56 items-end gap-2 overflow-x-auto pb-7">
                  {dashboard.monthly_volume.map((month) => (
                    <div key={month.month} className="flex min-w-[42px] flex-1 flex-col items-center justify-end gap-2 self-stretch">
                      <span className="text-xs font-semibold text-slate-700">{month.orders || ''}</span>
                      <div className="flex h-full w-full max-w-[34px] items-end rounded-t bg-slate-100">
                        <div className="w-full rounded-t bg-indigo-500 transition-all" style={{ height: `${Math.max((month.orders / monthlyMax) * 100, month.orders ? 8 : 0)}%` }} title={`${month.orders} orders, ${month.units} units`} />
                      </div>
                      <span className="whitespace-nowrap text-[10px] text-slate-500">{month.label.replace('20', "'")}</span>
                    </div>
                  ))}
                </div>
              ) : <EmptyState>No order history for this period.</EmptyState>}
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="font-bold text-slate-900">Quick opportunity signals</h3>
              <p className="text-sm text-slate-500">Useful prompts for account conversations</p>
              <div className="mt-5 space-y-3">
                <div className="rounded-lg bg-indigo-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Most ordered job</p>
                  <p className="mt-1 text-lg font-bold text-slate-900">{topJob?.name || 'No data yet'}</p>
                  {topJob && <p className="text-sm text-slate-600">{topJob.orders} orders · {topJob.units.toLocaleString()} units</p>}
                </div>
                <div className="rounded-lg bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Primary format</p>
                  <p className="mt-1 text-lg font-bold text-slate-900">{topFormat?.name || 'No data yet'}</p>
                  {topFormat && <p className="text-sm text-slate-600">{topFormat.orders} orders · {formatCurrency(topFormat.spend)}</p>}
                </div>
                <div className="rounded-lg bg-emerald-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Recent activity</p>
                  <p className="mt-1 text-lg font-bold text-slate-900">{latestMonthOrders} order{latestMonthOrders === 1 ? '' : 's'} this month</p>
                  <p className="text-sm text-slate-600">{activityMessage}</p>
                </div>
                <div className="rounded-lg bg-amber-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Follow-up prompt</p>
                  <p className="mt-1 text-sm text-slate-700">Review their less frequent job types and ask what they currently source elsewhere.</p>
                </div>
              </div>
            </div>
          </section>

          <section className="grid gap-6 xl:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="font-bold text-slate-900">Job type mix</h3>
              <p className="text-sm text-slate-500">What this customer orders most often</p>
              <div className="mt-5 space-y-4">
                {dashboard.job_types.length ? dashboard.job_types.map((job) => (
                  <div key={job.name}>
                    <div className="mb-1 flex justify-between gap-3 text-sm">
                      <span className="truncate font-medium text-slate-700">{job.name}</span>
                      <span className="whitespace-nowrap text-slate-500">{job.orders} orders · {job.units.toLocaleString()} units</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-indigo-500" style={{ width: `${(job.orders / jobMax) * 100}%` }} /></div>
                  </div>
                )) : <EmptyState>No job type data for this period.</EmptyState>}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="font-bold text-slate-900">Format and status mix</h3>
              <p className="text-sm text-slate-500">Where their work sits and what happens to it</p>
              <div className="mt-5 grid gap-6 sm:grid-cols-2">
                <div>
                  <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Formats</p>
                  <div className="space-y-3">
                    {dashboard.formats.length ? dashboard.formats.map((format) => (
                      <div key={format.name} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                        <span className="font-medium text-slate-700">{format.name}</span>
                        <span className="text-slate-500">{format.orders}</span>
                      </div>
                    )) : <EmptyState>No format data.</EmptyState>}
                  </div>
                </div>
                <div>
                  <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Statuses</p>
                  <div className="space-y-3">
                    {dashboard.statuses.length ? dashboard.statuses.map((status) => (
                      <div key={status.name} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(status.name)}`}>{status.name}</span>
                        <span className="text-slate-500">{status.orders}</span>
                      </div>
                    )) : <EmptyState>No status data.</EmptyState>}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="font-bold text-slate-900">Recent orders</h3>
                <p className="text-sm text-slate-500">The latest activity for this customer</p>
              </div>
              <Clock3 className="text-slate-400" size={20} aria-hidden="true" />
            </div>
            {dashboard.recent_orders.length ? (
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                    <tr><th className="px-3 py-3 font-semibold">Order</th><th className="px-3 py-3 font-semibold">Date</th><th className="px-3 py-3 font-semibold">Job types</th><th className="px-3 py-3 font-semibold">Status</th><th className="px-3 py-3 text-right font-semibold">Value</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {dashboard.recent_orders.map((order) => (
                      <tr key={order.id} className="text-slate-700">
                        <td className="px-3 py-3"><p className="font-medium text-slate-900">{order.name}</p><p className="text-xs text-slate-500">{order.quoteRef || order.id}</p></td>
                        <td className="whitespace-nowrap px-3 py-3">{formatDate(order.createdAt)}</td>
                        <td className="px-3 py-3">{order.jobTypes.join(', ') || 'Other'}</td>
                        <td className="px-3 py-3"><span className={`rounded-full px-2 py-1 text-xs font-medium ${statusClass(order.status)}`}>{order.status}</span></td>
                        <td className="whitespace-nowrap px-3 py-3 text-right font-medium">{formatCurrency(order.totalPrice)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <div className="mt-4"><EmptyState>No orders found for this period.</EmptyState></div>}
          </section>
        </>
      ) : null}
    </div>
  );
};

export default PartnerDashboard;
